import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from './../environments/environment';
import { service_controllers_methods } from './service_controllers_methods';
import { ActiveInactiveFor, PageKeyAccessType, AllowedPageAction, DecodedQrCode, GeneralConfigType, Graph, KeyValues20, KeyValues3, KeyValues5, KeyValueSet, KeyValueSetInt, LeftRightWrapper, LocalService, PageKey, Paging, Status, StatusWithId, Wrapper10, Wrapper3, Wrapper5, Wrappers3, Wrappers5, AccessRole, Pricing, InteractionType, AdditionalServiceType, BusinessRoleForUser, UserGuideType, CommunicationTemplateReq, AccessRange, KeyValueSets, DeviceInfoSetting, LocalNetworkSetting, LocalNetworkSettingWrapper, LocalWeightMachineName } from './local.service';
import { HttpRequestComponent } from '../app/shared/http-request/http-request.component';
import { CountryManagementService } from './country-management.service';
import { SellerManagementService } from './seller-management.service';
import { ProfileManagementService } from './profile-management.service';
import { Router } from '@angular/router';
import { Constant } from 'src/app/supporting/constants';

@Injectable({
  providedIn: 'root'
})
export class ConfigurationManagementService {
  private controllerName = service_controllers_methods.configuration.controller;
  private methods = service_controllers_methods.configuration.methods;

  constructor(private localService: LocalService, private httpService: HttpRequestComponent, private countryManagementService: CountryManagementService, private sellerManagementService: SellerManagementService, private profileManagementService: ProfileManagementService, private router: Router) { }

  public ensurePageActions(currentPageKey: PageKey, businessId: string = '') {
    this.localService.currentPageKey = currentPageKey;
    this.localService.setSelectedMenu(true);
    if (!this.localService.allowedPageActions.find(x => x.pageKey == this.localService.currentPageKey)) {
      this.getPageActions(this.localService.currentPageKey).subscribe(result => {
        this.localService.allowedPageActions.push(this.formatPageActions(result));
      });
    }
    this.localService.selectedPageName = this.localService.getPageKeyName();
    this.ensureUserRolesForBusiness(businessId);
  }

  public ensureUserRolesForBusiness(businessId: string, isRedirectRequired = false) {
    if (businessId && businessId != '0' && this.localService.selected.user) {
      if (!this.localService.businessRoleForUsers.find(x => x.businessId == businessId)) {
        this.sellerManagementService.getBusinessRelationShipRolesForUser(businessId).subscribe(result => {
          this.localService.businessRoleForUsers.push({
            businessId: businessId,
            userId: this.localService.selected.user,
            roles: result
          } as BusinessRoleForUser);
          this.localService.onTriggerClass('after_access_check');
          if (isRedirectRequired) {
            this.router.navigate(['/seller', this.localService.basicParams.countryCode, this.localService.basicParams.userId, 2, businessId]);
          }
        });
      }
    }
  }

  public ensurePricing() {
    if (!this.localService.pricing) {
      let defacultCountry = this.localService.countryCodes.find(x => x.v2 == this.localService.selected.countryCode);
      let ctryId = defacultCountry ? defacultCountry.key : this.localService.countryCodes[0].key;
      this.countryManagementService.getCountryPricing(ctryId, false).subscribe(result => {
        this.localService.pricing = [];
        result.forEach(item => {
          this.localService.pricing.push({
            chargeMethod: parseInt(item.v1),
            chargeForType: parseInt(item.v2),
            amount: item.v3 != '' ? parseFloat(item.v3) : 0
          } as Pricing);
        });
      });
    }
  }

  formatPageActions(data: KeyValueSetInt): AllowedPageAction {
    let rtn = { pageKey: this.localService.currentPageKey, isPageAllowed: data && data.key == 1, allowedActions: [] } as AllowedPageAction;
    if (rtn.isPageAllowed && data.value && data.value != '') {
      rtn.allowedActions = data.value.split(',').map(x => parseInt(x));
    }
    return rtn;
  }

  public getHostPathForFileUpload() {
    return environment.url.bulk_service + this.controllerName + this.methods.UploadFile;
  }

  public saveGeneralConfiguration(wrapper: Wrapper5): Observable<StatusWithId> {
    let body = {
      wrapper: wrapper,
      sel: this.localService.getSelected()
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.SaveGeneralConfiguration, body);
  }

  public saveGlobalConfiguration(wrapper: Wrapper5): Observable<StatusWithId> {
    let body = {
      wrapper: wrapper,
      sel: this.localService.getSelected()
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.SaveGlobalConfiguration, body);
  }

  public saveField(wrapper: any): Observable<StatusWithId> {
    let body = {
      wrapper: wrapper,
      sel: this.localService.getSelected()
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.SaveField, body);
  }

  public getFields(strFieldFor: string): Observable<KeyValues5[]> {
    let body = {
      strFieldFor: strFieldFor,
      sel: this.localService.getSelected()
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.GetFields, body);
  }

  public getConfiguration(id: string): Observable<Wrapper5> {
    let body = {
      strId: id,
      sel: this.localService.getSelected()
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.GetConfiguration, body);
  }

  public getField(id: string): Observable<Wrapper3> {
    let body = {
      strId: id,
      sel: this.localService.getSelected()
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.GetField, body);
  }

  public getGlobalConfiguration(id: string): Observable<Wrapper5> {
    let body = {
      strId: id,
      sel: this.localService.getSelected()
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.GetGlobalConfiguration, body);
  }

  public activeInactiveStatus(strId: string, actionFor: ActiveInactiveFor, businessId: string | undefined = undefined): Observable<Status> {
    let body = {
      strId: strId,
      sel: this.localService.getSelected(),
      iActionFor: parseInt(actionFor.toString()),
      bizId: businessId
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.ActiveInactiveStatus, body);
  }

  public getConfigurations(iConfigType: number, paging: Paging, hasCtryCode: boolean = false, isUpdateCountryCode: boolean = false, ctryCode: string = '', businessId: string | undefined = undefined): Observable<Wrappers5> {
    if (paging == null)
      paging = this.localService.getLastPagingSetup(GeneralConfigType.PagingGeneralConfig);
    return this.getConfigurationsBackground(iConfigType, paging, hasCtryCode, isUpdateCountryCode, ctryCode, businessId);
  }

  public getConfigurationsBackground(iConfigType: number, paging: Paging, hasCtryCode: boolean = false, isUpdateCountryCode: boolean = false, ctryCode: string = '', businessId: string | undefined = undefined): Observable<Wrappers5> {
    let body = {
      sel: this.localService.getSelected(),
      dt: { key: iConfigType.toString(), v1: hasCtryCode ? "1" : "0", v2: isUpdateCountryCode ? "1" : "0", v3: ctryCode, v4: businessId } as KeyValues5,
      pag: paging
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.GetConfigurations, body, paging != null && !paging.isAutoRefresh);
  }

  public getGlobalConfigurations(): Observable<KeyValues5[]> {
    let body = {
      sel: this.localService.getSelected()
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.GetGlobalConfigurations, body);
  }

  public getConfigurationsToDisplay(iConfigType: GeneralConfigType, isEnumAsId = true, isShowSpinner = true, languageCode?: string): Observable<KeyValueSet[]> {
    let body = {
      sel: this.localService.getSelected(languageCode),
      dt: { v1: iConfigType.toString(), v2: isEnumAsId ? "1" : "0" } as KeyValues3
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.GetConfigurationsToDisplay, body, isShowSpinner);
  }

  public getConfigurationsToDisplayMulti(iConfigTypes: GeneralConfigType[], isEnumAsId = true, isShowSpinner = true, languageCode?: string): Observable<KeyValueSets[]> {
    let body = {
      sel: this.localService.getSelected(languageCode),
      dt: { v2: isEnumAsId ? "1" : "0" } as KeyValues3,
      ids: iConfigTypes
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.GetConfigurationsToDisplayMulti, body, isShowSpinner);
  }

  public getGlobalConfigurationByCode(strConfigCode: string, businessId?: string): Observable<KeyValueSet> {
    let body = {
      sel: this.localService.getSelected(),
      dt: { v1: strConfigCode, v2: businessId } as KeyValues3,
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.GetGlobalConfigurationByCode, body);
  }

  public backUp(): any {
    let body = {
      sel: this.localService.getSelected()
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.Backup, body);
  }

  public runIndexes(): any {
    let body = {
      sel: this.localService.getSelected()
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.RunIndexes, body);
  }

  public ensureUniqueKeys(): any {
    let body = {
      sel: this.localService.getSelected()
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.EnsureUniqueKeys, body);
  }

  public generateRent(countryCode: string = '', accessRangeId?: string, accessRange?: AccessRange): any {
    let body = {
      sel: this.localService.getSelected(),
      dt: { key: countryCode, v1: accessRange.toString(), v2: accessRangeId } as KeyValues3
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.GenerateRent, body);
  }

  public getSummary(strId: string, summaryType: any, ctryCode: string = ''): Observable<KeyValues20> {
    let dt = { key: strId, value: summaryType } as KeyValueSet;
    let body = {
      sel: this.localService.getSelected(ctryCode),
      dt: dt,
      ctry: ctryCode
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.GetSummary, body);
  }

  public getGraph(graph: Graph, paging: Paging, value1?: string, value2?: string, value3?: string): Observable<Graph> {
    let body = {
      sel: this.localService.getSelected(),
      gr: graph,
      pag: paging,
      dt: { v1: value1 ?? '', v2: value2 ?? '', v3: value3 ?? '' } as KeyValues3
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.GetGraph, body, true, false, true);
  }

  public getExcelData(paging: Paging, data: any) {
    let body = {
      sel: this.localService.getSelected(),
      pag: paging,
      dt: data
    };
    return this.httpService.request_general_post_blob_response(this.controllerName + this.methods.GetExcelData, body, true, true);
  }

  public getQrDetails(decodedQrCode: DecodedQrCode): Observable<KeyValues3> {
    let body = {
      sel: this.localService.getSelected(),
      dt: { key: decodedQrCode.countryCode, v1: decodedQrCode.barCodeCategory.toString(), v2: decodedQrCode.value } as KeyValues3
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.GetQrDetails, body);
  }

  public getAllowedActionsSetup(pageKey: PageKey, accessRole: AccessRole): Observable<LeftRightWrapper> {
    let body = {
      sel: this.localService.getSelected(),
      dt: { v1: pageKey.toString(), v2: accessRole.toString() } as KeyValues3
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.GetAllowedActionsSetup, body);
  }

  public saveAlowedActions(lstItems: string[], pageKey: PageKey, accessRole: AccessRole): Observable<StatusWithId> {
    let body = {
      sel: this.localService.getSelected(),
      dt: { v1: pageKey.toString(), v2: accessRole.toString(), v3: lstItems.join(',') } as KeyValues3
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.SaveAlowedActions, body);
  }

  public getPageActions(pageKey: PageKey): Observable<KeyValueSetInt> {
    let body = {
      sel: this.localService.getSelected(),
      dt: { key: pageKey } as KeyValueSetInt
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.GetPageActionsForUser, body);
  }

  public getGuideTitlesToDisplay(isAll: boolean = false): Observable<KeyValueSet[]> {
    let body = {
      sel: this.localService.getSelected(),
      dt: { v1: isAll ? '1' : '0' } as KeyValues3
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.GetGuideTitlesToDisplay, body);
  }

  public saveGuide(data: Wrapper3): Observable<StatusWithId> {
    let body = {
      sel: this.localService.getSelected(),
      dt: data
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.SaveGuide, body);
  }

  public getGuide(type: UserGuideType, pageKey: number): Observable<Wrapper3> {
    let body = {
      sel: this.localService.getSelected(),
      dt: { v1: pageKey.toString(), v2: type.toString() } as KeyValues3
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.GetGuide, body);
  }

  public getGuideContent(type: UserGuideType, pageKey: number): Observable<KeyValueSet> {
    let body = {
      sel: this.localService.getSelected(),
      dt: { v1: pageKey.toString(), v2: type.toString() } as KeyValues3
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.GetGuideContent, body);
  }

  public getGalleries(paging: Paging): Observable<Wrappers3> {
    if (paging == null)
      paging = this.localService.getLastPagingSetup(GeneralConfigType.PagingGallery);

    let body = {
      sel: this.localService.getSelected(),
      pag: paging
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.GetGalleries, body, paging != null && !paging.isAutoRefresh);
  }

  public getNextOrderStartUp(strParentId: string): Observable<number> {
    let body = {
      sel: this.localService.getSelected(),
      dt: strParentId
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.GetNextOrderStartUp, body);
  }

  public saveStartup(wrapper: Wrapper10): Observable<StatusWithId> {
    let body = {
      wrapper: wrapper,
      sel: this.localService.getSelected()
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.SaveStartup, body);
  }

  public getStartups(strParentId: string): Observable<KeyValues5[]> {
    let body = {
      sel: this.localService.getSelected(),
      dt: strParentId == '0' ? '' : strParentId
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.GetStartups, body);
  }

  public getStartup(strId: string): Observable<Wrapper10> {
    let body = {
      sel: this.localService.getSelected(),
      dt: strId
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.GetStartUp, body);
  }

  public getStartUpsToDisplay(): Observable<KeyValues5[]> {
    let body = {
      sel: this.localService.getSelected()
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.GetStartUpsToDisplay, body);
  }

  public isUserHasAccess(): Observable<Status> {
    let body = {
      sel: this.localService.getSelected()
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.IsUserHasAccess, body);
  }

  public getPageKeysRoleAccess(pageKeyAccessType: PageKeyAccessType): Observable<KeyValueSetInt[]> {
    let body = {
      sel: this.localService.getSelected(),
      dt: { v1: pageKeyAccessType.toString() } as KeyValues3
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.GetPageKeysRoleAccess, body);
  }

  public getAccessFile() {
    let body = {
      sel: this.localService.getSelected()
    };
    return this.httpService.request_general_post_blob_response(this.controllerName + this.methods.GetAccessFile, body, true, true);
  }

  public fixAccessRoleForPage(): Observable<StatusWithId> {
    let body = {
      sel: this.localService.getSelected()
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.FixAccessRoleForPage, body);
  }

  public getZippedGuides() {
    let body = {
      sel: this.localService.getSelected()
    };
    return this.httpService.request_general_post_blob_response(this.controllerName + this.methods.GetZippedGuides, body);
  }

  public downloadGuidesManual(pageKey: PageKey = PageKey.None) {
    let body = {
      sel: this.localService.getSelected(),
      dt: { v1: pageKey.toString() } as KeyValues3
    };
    return this.httpService.request_general_post_blob_response(this.controllerName + this.methods.DownloadGuidesManual, body, true, true);
  }

  public downloadGuidesTitlesManual() {
    let body = {
      sel: this.localService.getSelected()
    };
    return this.httpService.request_general_post_blob_response(this.controllerName + this.methods.DownloadGuidesTitlesManual, body);
  }

  public getPaymentsGateway(strBusinessId: string, interactionType: InteractionType, paging: Paging): Observable<Wrappers5> {
    let body = {
      sel: this.localService.getSelected(),
      dt: { v1: strBusinessId, v2: interactionType.toString() } as KeyValues3,
      pag: paging
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.GetPaymentsGateway, body);
  }

  public saveOnlyLanguagesConfig(wrapper: Wrapper3): Observable<StatusWithId> {
    let body = {
      sel: this.localService.getSelected(),
      dt: wrapper
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.SaveOnlyLanguagesConfig, body);
  }

  public getOnlyLanguagesConfig(pageKey: PageKey, paging: Paging, isActiveOnly: boolean = false, isShowSpinner = true): Observable<Wrappers3> {
    let body = {
      sel: this.localService.getSelected(),
      dt: { v1: pageKey.toString(), v2: isActiveOnly ? "1" : "0" } as KeyValues3,
      pag: paging
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.GetOnlyLanguagesConfig, body, isShowSpinner);
  }

  public saveGetStartedMenus(strId: string, ids: number[]): Observable<StatusWithId> {
    let body = {
      sel: this.localService.getSelected(),
      dt: { v1: strId } as KeyValues3,
      ids: ids
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.SaveGetStartedMenus, body);
  }

  public getGetStartedMenus(strId: string): Observable<number[]> {
    let body = {
      sel: this.localService.getSelected(),
      dt: { v1: strId } as KeyValues3
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.GetGetStartedMenus, body);
  }

  public applyGetStartedSettings(ids: string[]): Observable<StatusWithId> {
    let body = {
      sel: this.localService.getSelected(),
      ids: ids
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.ApplyGetStartedSettings, body);
  }

  public getTechnicalLogs(paging: Paging): Observable<Wrappers3> {
    let body = {
      sel: this.localService.getSelected(),
      pag: paging
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.GetTechnicalLogs, body);
  }

  public getAdditionalServiceDetails(businessId: string): Observable<KeyValues3[]> {
    let body = {
      sel: this.localService.getSelected(),
      dt: { v1: businessId } as KeyValues3
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.GetAdditionalServiceDetails, body);
  }

  public activateAdditionalService(businessId: string, additionalServiceType: AdditionalServiceType, isActivate: boolean): Observable<StatusWithId> {
    let body = {
      sel: this.localService.getSelected(),
      dt: { v1: businessId, v2: additionalServiceType.toString(), v3: isActivate ? "1" : "0" } as KeyValues3
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.ActivateAdditionalService, body);
  }

  public isVersionMatched(): Observable<StatusWithId> {
    let body = {
      dt: { v1: Constant.Version } as KeyValues3
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.IsVersionMatched, body, false);
  }

  public getCommunicationTemplateIds(reqDt: CommunicationTemplateReq): Observable<KeyValues3[]> {
    let body = {
      reqDt: reqDt
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.GetCommunicationTemplateIds, body);
  }

  public saveCommunicationTemplateIds(reqDt: CommunicationTemplateReq, items: KeyValues3[]): Observable<KeyValues3[]> {
    let body = {
      sel: this.localService.getSelected(),
      reqDt: reqDt,
      dts: items
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.SaveCommunicationTemplateIds, body);
  }

  public copyAllGuideContentsToEnglish(guideId?: string): Observable<StatusWithId> {
    let body = {
      sel: this.localService.getSelected(),
      dt: { v1: guideId ?? '' } as KeyValues3
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.CopyAllGuideContentsToEnglish, body);
  }

  public onEnsureServiceDown() {
    this.httpService.onEnsureServiceDown();
  }

  public getReques(isBackUpService: boolean = false): Observable<StatusWithId> {
    return this.httpService.request_general_post(this.controllerName + this.methods.GetReques, {}, true, false, false, isBackUpService);
  }

  public saveDeviceInfoSetting(item: DeviceInfoSetting): Observable<StatusWithId> {
    let body = {
      sel: this.localService.getSelected(),
      dt: item
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.SaveDeviceInfoSetting, body);
  }

  public getDeviceInfoSetting(deviceId: string, businessId: string): Observable<DeviceInfoSetting> {
    let body = {
      sel: this.localService.getSelected(),
      dt: { v1: deviceId, v2: businessId } as KeyValues3
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.GetDeviceInfoSetting, body);
  }

  public getDeviceInfoSettings(businessId: string, isActive: boolean): Observable<KeyValues5[]> {
    let body = {
      sel: this.localService.getSelected(),
      dt: { v1: businessId, v2: isActive ? "1" : "0" } as KeyValues3
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.GetDeviceInfoSettings, body);
  }

  public assignUserToDevice(businessId: string, userId: string, deviceId: string): Observable<StatusWithId> {
    let body = {
      sel: this.localService.getSelected(),
      dt: { v1: businessId, v2: userId, v3: deviceId } as KeyValues3
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.AssignUserToDevice, body);
  }

  public getLocalNetworkSettings(businessId: string, isActive: boolean): Observable<KeyValues5[]> {
    let body = {
      sel: this.localService.getSelected(),
      dt: { v1: businessId, v2: isActive ? "1" : "0" } as KeyValues3
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.GetLocalNetworkSettings, body);
  }

  public getLocalNetworkSetting(networkId: string, businessId: string): Observable<LocalNetworkSettingWrapper> {
    let body = {
      sel: this.localService.getSelected(),
      dt: { v1: networkId, v2: businessId } as KeyValues3
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.GetLocalNetworkSetting, body);
  }

  public saveLocalNetworkSetting(item: LocalNetworkSettingWrapper): Observable<StatusWithId> {
    let body = {
      sel: this.localService.getSelected(),
      dt: item
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.SaveLocalNetworkSetting, body);
  }

  public getActiveLocalPrinterNamesMin(businessId: string): Observable<KeyValues5[]> {
    let body = {
      sel: this.localService.getSelected(),
      dt: { v1: businessId } as KeyValues3
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.GetActiveLocalPrinterNamesMin, body);
  }

  public GetActiveLocalWeightMachineNamesByBusinessId(businessId: string): Observable<LocalWeightMachineName[]> {
    let body = {
      sel: this.localService.getSelected(),
      dt: { v1: businessId } as KeyValues3
    };
    return this.httpService.request_general_post(this.controllerName + this.methods.GetActiveLocalWeightMachineNamesByBusinessId, body);
  }
}
